import { useEffect, useState } from "react";
import { Pencil, Plus, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { brl } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const EMPTY = { code: "", kind: "percent", value: "", partner: "", min_subtotal: "", max_uses: "", active: true };

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        kind: form.kind,
        value: Number(form.value),
        partner: form.partner,
        min_subtotal: Number(form.min_subtotal || 0),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        active: form.active,
      };
      if (editing) await api.put(`/admin/coupons/${editing}`, body);
      else await api.post("/admin/coupons", body);
      toast.success(editing ? "Cupom atualizado" : "Cupom criado");
      setForm(EMPTY); setEditing(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({ code: c.code, kind: c.kind, value: c.value, partner: c.partner, min_subtotal: c.min_subtotal || "", max_uses: c.max_uses || "", active: c.active });
  };

  const remove = async (c) => {
    if (!window.confirm(`Remover o cupom ${c.code}?`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Cupom removido"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.id}`, { code: c.code, kind: c.kind, value: c.value, partner: c.partner, min_subtotal: c.min_subtotal, max_uses: c.max_uses, active: !c.active });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Marketing</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Cupons & parcerias</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-2" data-testid="coupons-list">
          {coupons.length === 0 && <p className="text-sm text-slate-500">Nenhum cupom criado ainda.</p>}
          {coupons.map((c) => (
            <div key={c.id} data-testid={`coupon-row-${c.code}`} className="rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-xl font-bold tracking-wide">{c.code}
                    <span className="ml-2 align-middle text-sm font-semibold text-primo-400">
                      {c.kind === "percent" ? `${c.value}% off` : `${brl(c.value)} off`}
                    </span>
                  </p>
                  {c.partner && <p className="text-sm text-slate-400">Parceiro: {c.partner}</p>}
                  <p className="text-xs text-slate-500">
                    {c.min_subtotal > 0 ? `Pedido mín. ${brl(c.min_subtotal)} · ` : ""}
                    {c.max_uses ? `Limite ${c.max_uses} usos` : "Usos ilimitados"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Switch data-testid={`coupon-active-${c.code}`} checked={c.active} onCheckedChange={() => toggle(c)} />
                  <button data-testid={`edit-coupon-${c.code}`} onClick={() => edit(c)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-coupon-${c.code}`} onClick={() => remove(c)} className="grid h-9 w-9 place-items-center rounded-lg text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-white/[0.06] pt-3 text-center">
                <div><p className="text-xs text-slate-500">Usos</p><p data-testid={`coupon-uses-${c.code}`} className="font-display text-lg font-bold">{c.uses}</p></div>
                <div><p className="text-xs text-slate-500">Desconto dado</p><p data-testid={`coupon-discount-${c.code}`} className="font-display text-lg font-bold text-red-400">{brl(c.discount_total)}</p></div>
                <div><p className="text-xs text-slate-500">Vendas geradas</p><p data-testid={`coupon-revenue-${c.code}`} className="font-display text-lg font-bold text-emerald-400">{brl(c.revenue_total)}</p></div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="h-fit space-y-3 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            <Ticket className="h-5 w-5 text-primo-400" /> {editing ? "Editar cupom" : "Novo cupom"}
          </h2>
          <div><Label>Código</Label><Input required data-testid="coupon-code-input" placeholder="PRIMO10" value={form.code} onChange={set("code")} className="uppercase" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Tipo</Label>
              <select data-testid="coupon-kind-select" value={form.kind} onChange={set("kind")}
                className="mt-1 w-full rounded-xl border border-white/[0.08] bg-[#141414] px-3 py-2 text-sm outline-none focus:border-primo-500/60">
                <option value="percent">Porcentagem (%)</option>
                <option value="fixed">Valor fixo (R$)</option>
              </select></div>
            <div><Label>Valor</Label><Input required type="number" min={0.01} step="0.01" data-testid="coupon-value-input" value={form.value} onChange={set("value")} /></div>
          </div>
          <div><Label>Parceiro (opcional)</Label><Input data-testid="coupon-partner-input" placeholder="Ex.: Barbearia do João" value={form.partner} onChange={set("partner")} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Pedido mínimo</Label><Input type="number" min={0} step="0.01" data-testid="coupon-min-input" placeholder="0,00" value={form.min_subtotal} onChange={set("min_subtotal")} /></div>
            <div><Label>Limite de usos</Label><Input type="number" min={1} data-testid="coupon-max-uses-input" placeholder="ilimitado" value={form.max_uses} onChange={set("max_uses")} /></div>
          </div>
          <div className="flex items-center gap-3"><Switch data-testid="coupon-active-switch" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} /><Label>Ativo</Label></div>
          <Button data-testid="coupon-save-button" disabled={saving} className="w-full rounded-full font-bold">
            <Plus className="mr-1 h-4 w-4" /> {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar cupom"}
          </Button>
          {editing && (
            <button type="button" data-testid="coupon-cancel-edit" onClick={() => { setEditing(null); setForm(EMPTY); }}
              className="w-full text-xs text-slate-500 hover:text-white">Cancelar edição</button>
          )}
        </form>
      </div>
    </div>
  );
}
